/**
 * Petits graphiques en SVG, faits main. Aucune bibliothèque : quatre courbes et
 * des sparklines ne justifient pas 200 ko de dépendance, et le SVG suit les
 * tokens du design system sans couche d'adaptation.
 *
 * Le vocabulaire visuel vient de « Skills Matrix Admin Dashboards » : grille
 * horizontale claire, courbes de 2px, point plein sur la dernière valeur.
 */

export const TEINTES_SERIES = ['#004F99', '#05BAEE', '#D6007F', '#C4CADF', '#AC67FF'] as const;

export const VERT_HAUSSE = '#0F8A4D';
export const ROUGE_BAISSE = '#B4295E';

export type Serie = {
  cle: string;
  libelle: string;
  teinte: string;
  valeurs: (number | null)[];
};

function cheminPolyline(
  valeurs: readonly (number | null)[],
  largeur: number,
  hauteur: number,
  max: number,
  marge: { gauche: number; haut: number; bas: number },
): string {
  const points: string[] = [];
  const pas = valeurs.length > 1 ? (largeur - marge.gauche - 8) / (valeurs.length - 1) : 0;

  valeurs.forEach((valeur, index) => {
    if (valeur === null) return;
    const x = marge.gauche + index * pas;
    const utile = hauteur - marge.haut - marge.bas;
    const y = marge.haut + utile - (max === 0 ? 0 : (valeur / max) * utile);
    points.push(`${Math.round(x * 10) / 10},${Math.round(y * 10) / 10}`);
  });

  return points.join(' ');
}

export function GraphiqueLignes({
  series,
  etiquettes,
  max,
  unite,
  hauteur = 232,
}: {
  series: Serie[];
  etiquettes: string[];
  max?: number;
  unite?: string;
  hauteur?: number;
}): JSX.Element {
  const largeur = 660;
  const marge = { gauche: 40, haut: 20, bas: 32 };

  const valeurMax = Math.max(
    1,
    max ??
      Math.ceil(
        Math.max(
          ...series.flatMap((s) => s.valeurs.map((v) => v ?? 0)),
          1,
        ) * 1.1,
      ),
  );

  const lignes = 4;
  const utile = hauteur - marge.haut - marge.bas;

  return (
    <svg
      viewBox={`0 0 ${largeur} ${hauteur}`}
      className="graphique"
      role="img"
      aria-label={`${series.map((s) => s.libelle).join(', ')} — ${etiquettes[0]} to ${etiquettes[etiquettes.length - 1]}`}
    >
      <g stroke="var(--border)">
        {Array.from({ length: lignes + 1 }, (_, i) => {
          const y = marge.haut + (utile / lignes) * i;
          return <line key={i} x1={marge.gauche} y1={y} x2={largeur - 8} y2={y} />;
        })}
      </g>

      <g className="graphique__axe" textAnchor="end">
        {Array.from({ length: lignes + 1 }, (_, i) => {
          const y = marge.haut + (utile / lignes) * i;
          const valeur = Math.round((valeurMax / lignes) * (lignes - i) * 10) / 10;
          return (
            <text key={i} x={marge.gauche - 8} y={y + 4}>
              {valeur}
              {unite ?? ''}
            </text>
          );
        })}
      </g>

      <g className="graphique__axe" textAnchor="middle">
        {etiquettes.map((etiquette, index) => {
          const pas = etiquettes.length > 1 ? (largeur - marge.gauche - 8) / (etiquettes.length - 1) : 0;
          return (
            <text key={etiquette} x={marge.gauche + index * pas} y={hauteur - 10}>
              {etiquette}
            </text>
          );
        })}
      </g>

      {series.map((serie) => (
        <polyline
          key={serie.cle}
          fill="none"
          stroke={serie.teinte}
          strokeWidth="2"
          points={cheminPolyline(serie.valeurs, largeur, hauteur, valeurMax, marge)}
        />
      ))}

      {series.map((serie) => {
        const dernier = serie.valeurs[serie.valeurs.length - 1];
        if (dernier === null || dernier === undefined) return null;
        const pas =
          serie.valeurs.length > 1 ? (largeur - marge.gauche - 8) / (serie.valeurs.length - 1) : 0;
        const x = marge.gauche + (serie.valeurs.length - 1) * pas;
        const y = marge.haut + utile - (valeurMax === 0 ? 0 : (dernier / valeurMax) * utile);
        return (
          <circle key={serie.cle} cx={x} cy={y} r="4" fill="#FFFFFF" stroke={serie.teinte} strokeWidth="2" />
        );
      })}
    </svg>
  );
}

/** Sparkline : la forme compte, pas l'échelle. Pas d'axes, pas de graduations. */
export function Sparkline({
  valeurs,
  teinte,
  hauteur = 36,
  titre,
}: {
  valeurs: (number | null)[];
  teinte: string;
  hauteur?: number;
  titre?: string;
}): JSX.Element {
  const largeur = 120;
  const max = Math.max(1, ...valeurs.map((v) => v ?? 0));
  const points = cheminPolyline(valeurs, largeur, hauteur, max, { gauche: 0, haut: 4, bas: 4 });

  return (
    <svg viewBox={`0 0 ${largeur} ${hauteur}`} className="sparkline" role="img" aria-label={titre ?? ''}>
      <polyline fill="none" stroke={teinte} strokeWidth="2" points={points} />
    </svg>
  );
}

/** Variation signée, avec la couleur qui va avec. Zéro reste neutre. */
export function Variation({ valeur, suffixe }: { valeur: number; suffixe?: string }): JSX.Element {
  const couleur = valeur > 0 ? VERT_HAUSSE : valeur < 0 ? ROUGE_BAISSE : 'var(--fg-3)';
  const signe = valeur > 0 ? '+' : valeur < 0 ? '−' : '';
  return (
    <span className="mono" style={{ fontSize: 12, color: couleur }}>
      {signe}
      {Math.abs(valeur)}
      {suffixe ?? ''}
    </span>
  );
}

/** Les paliers atteints, en quatre segments — la « barre de niveau » du design. */
export function BarreNiveau({ niveau }: { niveau: number | null }): JSX.Element {
  const paliers = [
    { seuil: 1, couleur: '#C4CADF' },
    { seuil: 2, couleur: '#05BAEE' },
    { seuil: 3, couleur: '#004F99' },
    { seuil: 4, couleur: '#D6007F' },
    { seuil: 5, couleur: '#1F1F1F' },
  ];

  return (
    <span className="barre-niveau" aria-hidden="true">
      {paliers.map((p) => (
        <span
          key={p.seuil}
          style={{ background: (niveau ?? -1) >= p.seuil ? p.couleur : 'var(--surface-3)' }}
        />
      ))}
    </span>
  );
}
